import type {CampaignADto} from '@cohort/admin-schemas/campaign';
import type {JourneyParticipationADto} from '@cohort/admin-schemas/journeyParticipation';
import JourneyParticipationStatusBadge from '@cohort/merchants/components/campaigns/journeys/JourneyParticipationStatusBadge';
import JourneyParticipationStepper from '@cohort/merchants/components/campaigns/journeys/JourneyParticipationStepper';
import type {TableEmptyState} from '@cohort/merchants/components/Table';
import Table from '@cohort/merchants/components/Table';
import {useJourneySteps} from '@cohort/merchants/hooks/api/Campaigns';
import {useCurrentMerchant} from '@cohort/merchants/hooks/contexts/currentMerchant';
import {formatDate} from '@cohort/shared/utils/format';
import {useAutoAnimate} from '@formkit/auto-animate/react';
import {Disclosure} from '@headlessui/react';
import {CaretDown, CaretUp} from '@phosphor-icons/react';
import React, {Fragment} from 'react';
import {useTranslation} from 'react-i18next';

type JourneyParticipationsTableProps = {
  journeyParticipations: Array<JourneyParticipationADto>;
  isLoading: boolean;
  emptyState?: TableEmptyState;
  campaign: CampaignADto;
};

const JourneyParticipationsTable: React.FC<JourneyParticipationsTableProps> = ({
  journeyParticipations,
  isLoading,
  emptyState,
  campaign,
}) => {
  const merchant = useCurrentMerchant();
  const {t} = useTranslation('pages', {
    keyPrefix: 'campaigns.campaign.activity.journey.journeyParticipationsTable',
  });
  const {data: journeySteps} = useJourneySteps(merchant.id, campaign.id);
  const [parent] = useAutoAnimate();

  const headers = [
    {label: t('tableHeaderEmail')},
    {label: t('tableHeaderDate')},
    {label: t('tableHeaderStatus')},
    {label: t('tableHeaderSteps')},
  ];

  return (
    <Table headers={headers} isLoading={isLoading} emptyState={emptyState}>
      {journeyParticipations.map(journeyParticipation => (
        <Disclosure key={journeyParticipation.id}>
          {({open}) => (
            <Fragment>
              <tr key={journeyParticipation.id} className="py-2">
                <td className="w-1/4 py-4 pr-3 text-sm font-medium">
                  {journeyParticipation.userEmail}
                </td>
                <td className="flex w-1/4 items-center gap-2 whitespace-nowrap py-4 pr-3 text-sm text-slate-500">
                  {formatDate(journeyParticipation.createdAt)}
                </td>
                <td className="w-1/4 whitespace-nowrap py-4 pr-3 text-sm text-slate-500">
                  <JourneyParticipationStatusBadge status={journeyParticipation.status} />
                </td>
                <td className="w-1/4 whitespace-nowrap py-4 pr-3 text-sm text-slate-500">
                  <Disclosure.Button className="flex flex-row items-center space-x-4">
                    <span>
                      {journeyParticipation.stepCompletionAttempts.filter(
                        attempt => attempt.success
                      ).length +
                        '/' +
                        journeySteps?.length}
                    </span>
                    {open ? (
                      <span className="font-medium text-primary">
                        {t('buttonHideDetails')}
                        <CaretUp className="ml-1 inline h-4 w-4" />
                      </span>
                    ) : (
                      <span className="font-medium text-primary">
                        {t('buttonShowDetails')}
                        <CaretDown className="ml-1 inline h-4 w-4" />
                      </span>
                    )}
                  </Disclosure.Button>
                </td>
              </tr>
              <tr ref={parent}>
                <Disclosure.Panel as="td" colSpan={4}>
                  <JourneyParticipationStepper journeyParticipation={journeyParticipation} />
                </Disclosure.Panel>
              </tr>
            </Fragment>
          )}
        </Disclosure>
      ))}
    </Table>
  );
};

export default JourneyParticipationsTable;
